/**
 * A bounded work queue for bulk actions.
 *
 * None of the invoice endpoints accept more than one guid, so every bulk action
 * is N requests. `Promise.all` over the whole selection fires all N at once —
 * with a 50-row page that is 50 simultaneous requests, which browsers serialise
 * anyway (6 per host), buries the API in a burst, and turns one expired token
 * into 50 parallel refresh attempts.
 *
 * This runs a fixed number of workers that pull from a shared cursor, so at most
 * `concurrency` requests are ever in flight. `Promise.all` still does the
 * waiting — over the workers, not over the tasks.
 *
 * Never rejects: one failed invoice must not abandon the rest of the selection,
 * so results come back in `Promise.allSettled` shape, in input order.
 */
export async function runQueue(items, worker, { concurrency = 4, onProgress } = {}) {
  const list = [...items];
  const results = new Array(list.length);
  let cursor = 0;
  let completed = 0;

  async function pump() {
    while (cursor < list.length) {
      const index = cursor;
      cursor += 1;

      try {
        results[index] = { status: "fulfilled", value: await worker(list[index], index) };
      } catch (reason) {
        results[index] = { status: "rejected", reason };
      }

      completed += 1;
      onProgress?.(completed, list.length);
    }
  }

  const workerCount = Math.max(1, Math.min(concurrency, list.length));
  await Promise.all(Array.from({ length: workerCount }, pump));

  return results;
}

/** Splits settled results into counts plus the reasons, for reporting. */
export function summarizeQueue(results) {
  const failures = results.filter((result) => result.status === "rejected");

  return {
    total: results.length,
    succeeded: results.length - failures.length,
    failed: failures.length,
    failures,
  };
}

/** "1 invoice" / "3 invoices" — bulk toasts all need this. */
export function pluralizeInvoices(count) {
  return `${count} invoice${count === 1 ? "" : "s"}`;
}
